import { KeyEnum } from 'app/hooks/useKeyBinder';
import { useCreatePopup } from 'Components/Popups';
import Options from 'Components/Select/Options';
import { last } from 'lodash';
import { ReactNode, useRef, useState } from 'react';
import CancelSelectionButton from '../CancelSelectionButton';
import DropDownButton from '../DropDownButton';
import SelectInputWindow from '../SelectInputWindow';

export default function MultiSelectSearchDropDown<T>({
  value = [],
  onChange,
  options,
  id,
  optionItemSkin,
  itemId = 'id' as any,
  selectedItemSkin,
  searchFilter = (search, options) => options,
  optionItemDefaultStyling = true,
  className,
  placeholder = 'Search...',
  maxheight = 'max-h-20',
  prefix,
  showSelectedItems = true,
}: {
  id?: string;
  value?: T[];
  itemId?: keyof T;
  onChange: (cahnges: T[] | undefined) => void;
  searchFilter?: (text: string, pool: T[]) => T[];
  options: T[];
  optionItemSkin: (option: T, isActive: boolean) => ReactNode;
  selectedItemSkin?: (item: T) => ReactNode;
  optionItemDefaultStyling?: boolean;
  className?: string;
  placeholder?: string;
  maxheight?: string;
  prefix?: React.ReactNode;
  showSelectedItems?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const SelectPopup = useCreatePopup();

  const selectedItems = value;
  const sIds = selectedItems.filter((i) => !!i).map((i) => i[itemId]);
  const selectableItems = options.filter((i) => !sIds.includes(i[itemId]));
  const optionItems = searchFilter(search, selectableItems).filter(
    (o) => !sIds.includes(o[itemId]),
  );

  const deleteItem = (item: T) => {
    onChange(selectedItems.filter((i) => i[itemId] !== item[itemId]));
  };

  const focusTextInput = () => {
    inputRef.current?.focus();
    SelectPopup?.open();
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current?.scrollHeight;
    }
  };
  const addItem = (item: T) => {
    onChange(selectedItems.concat(item));
    setSearch('');
    focusTextInput();
    SelectPopup?.close();
  };

  const onKeyDownHandle = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      ![KeyEnum.ENTER, KeyEnum.ESCAPE, KeyEnum.BACKSPACE].includes(
        e.key as KeyEnum,
      )
    )
      focusTextInput();

    if (e.key === KeyEnum.ESCAPE) setSearch('');

    if (
      e.key === KeyEnum.BACKSPACE &&
      search === '' &&
      selectedItems.length > 0
    )
      deleteItem(last(selectedItems) as T);
  };

  const onChangeHandle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value.trimLeft());
  };

  const onClearHandle = () => {
    SelectPopup?.close();
    setSearch('');
    onChange(undefined);
  };

  return (
    <>
      <div
        ref={SelectPopup.setAnchor}
        className={`form__input flex justify-between items-center ${className}`}
        onClick={() => focusTextInput()}
      >
        <div
          ref={containerRef}
          className={`flex-1 flex ${maxheight} overflow-y-auto py-1 gap-x-2 text-sm items-center gap-y-1.5 flex-wrap`}
        >
          {showSelectedItems &&
            selectedItems.map((item, index) => (
              <div
                key={item[itemId] as unknown as string}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteItem(item);
                }}
              >
                {selectedItemSkin && selectedItemSkin(item)}
              </div>
            ))}

          <div className="flex-1 flex">
            {prefix && <span className="w-1/12 mr-4">{prefix}</span>}
            <input
              id={id}
              type="text"
              ref={inputRef}
              className="outline-none focus:outline-none placeholder-black "
              value={search}
              style={{
                width: search.length === 0 ? '1px' : search.length + 2 + 'ch',
              }}
              onChange={onChangeHandle}
              onKeyDown={onKeyDownHandle}
            />
            {selectedItems.length === 0 && !search && (
              <div className="text-gray-light">{placeholder}</div>
            )}
          </div>
        </div>
        <div className="flex">
          <CancelSelectionButton onClick={onClearHandle} />
          <DropDownButton />
        </div>
      </div>

      <SelectPopup.Component matchAnchorWidth={true}>
        <SelectInputWindow>
          <Options onSelect={addItem} optionItems={optionItems}>
            {optionItemSkin}
          </Options>
        </SelectInputWindow>
      </SelectPopup.Component>
    </>
  );
}
